import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Tag } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function CategoriasList() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    // Debounce manual
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarDatos = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/parametrizacion/categorias?page=${page}&size=${pageSize}&searchTerm=${debouncedSearch}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error al cargar datos');
            const result = await response.json();
            setData(result.content);
            setTotalPages(result.totalPages);
            setTotalElements(result.totalElements);
            setError(null);
        } catch (error) {
            setError('No se pudieron cargar las categorías.');
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch, API_URL]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
            try {
                const response = await fetch(`${API_URL}/parametrizacion/categorias/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    toast.success('Categoría eliminada');
                    cargarDatos();
                } else {
                    throw new Error('Error al eliminar');
                }
            } catch (error) {
                toast.error('No se pudo eliminar la categoría');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-[#2d4a5d] p-3 flex justify-between items-center text-white rounded-t-sm shadow-md">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion/personas')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-sm font-bold tracking-tight uppercase tracking-wider text-slate-100">Mantenimiento: Catálogo de Categorías de Socio</h1>
                </div>

                <Link
                    to="/parametrizacion/categorias/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[11px] font-bold px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm uppercase"
                >
                    <PlusCircle className="w-3.5 h-3.5" />
                    NUEVA CATEGORÍA
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Mostrar:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm disabled:opacity-50"
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-400 italic">registros por página</span>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar categoría..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Table Area */}
            <div className="overflow-x-auto relative">
                {error && (
                    <div className="m-4 p-4 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b border-gray-200 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-6 py-3 border-r border-gray-100 w-16 text-center">ID</th>
                            <th className="px-6 py-3 border-r border-gray-100">Nombre de la Categoría</th>
                            <th className="px-6 py-3 border-r border-gray-100">Descripción Detallada</th>
                            <th className="px-6 py-3 text-center w-48">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-600">
                        {loading && data.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-12 text-center">
                                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">
                                    No se encontraron categorías.
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.id} className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-3 border-r border-gray-100 text-center text-gray-400 text-xs font-mono">
                                        {item.id}
                                    </td>
                                    <td className="px-6 py-3 border-r border-gray-100 font-bold text-gray-700 uppercase">
                                        {item.nombreCategoria}
                                    </td>
                                    <td className="px-6 py-3 border-r border-gray-100 text-gray-500 text-xs">
                                        {item.descripcion}
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                        <div className="flex justify-center items-center gap-2">
                                            <Link
                                                to={`/parametrizacion/categorias/${item.id}/editar`}
                                                className="text-blue-600 hover:text-blue-800 font-bold text-[11px] uppercase tracking-tighter"
                                            >
                                                EDITAR
                                            </Link>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-600 hover:text-red-800 font-bold text-[11px] uppercase tracking-tighter"
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginator Footer */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-xs">
                <div className="text-gray-400">
                    Mostrando <span className="font-bold text-gray-600">{startItem} a {endItem}</span> de <span className="font-bold text-gray-600">{totalElements}</span> registros
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setPage(0)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                    >
                        <ChevronsLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>

                    <button className="px-3 py-1 border border-blue-600 bg-blue-600 text-white font-bold rounded-sm shadow-sm">
                        {page + 1}
                    </button>
                    <span className="text-gray-400 text-[10px] px-1 uppercase font-bold tracking-tight">de {totalPages || 1}</span>

                    <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                    >
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page >= totalPages - 1 || totalPages === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                    >
                        <ChevronsRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
